import { SyscoSelfServiceActions } from '../../actions';
import type {
	IAsynchronousAction,
	IManageLocations,
	INationalAccountCustomer,
	ISyscoCreateLocationState,
	ISyscoManageCustomerState,
	ISyscoManageLocationsState,
	ISyscoSelfServiceState,
} from '../../appstate';
import type { IDistributionCenter } from '../../domain';
import * as GeneralReducers from '../GeneralReducers';

const { CustomerManagementActions, LocationManagementActions } =
	SyscoSelfServiceActions;

export const initialManageCustomerState: ISyscoManageCustomerState = {
	currentPage: 1,
	itemsPerPage: 20,
	customers: {
		totalResults: 0,
		totalPages: 0,
		payload: [],
		inProcess: false,
		error: false,
	},
	searchString: '',
};

export const initialCreateCustomerState: IAsynchronousAction = {
	inProcess: false,
	error: false,
};

export const initialManageLocationsState: ISyscoManageLocationsState = {
	currentPage: 1,
	itemsPerPage: 20,
	locations: {
		totalResults: 0,
		totalPages: 0,
		payload: [],
		inProcess: false,
		error: false,
	},
	customer: {
		inProcess: false,
		error: false,
		payload: null,
	},
	massImportLocations: {
		inProcess: false,
		error: false,
	},
	isMassImportRequestInProgress: {
		payload: false,
		inProcess: false,
		error: false,
	},
};

export const initialCreateLocationState: ISyscoCreateLocationState = {
	inProcess: false,
	error: false,
	customer: {
		payload: null,
		inProcess: false,
		error: false,
	},
	distributionCenters: {
		payload: null,
		inProcess: false,
		error: false,
	},
};

const initialState: ISyscoSelfServiceState = {
	manageCustomers: initialManageCustomerState,
	createCustomer: initialCreateCustomerState,
	manageLocations: initialManageLocationsState,
	createLocation: initialCreateLocationState,
};

export const _childReducers = {
	manageCustomersReducer: GeneralReducers.CreateResultSetReducer<
		INationalAccountCustomer[]
	>({
		startAction: CustomerManagementActions.SELF_SERVICE_GET_CUSTOMERS,
	}),
	createCustomerReducer: GeneralReducers.CreateAsynchronousActionReducer({
		startAction: CustomerManagementActions.SELF_SERVICE_CREATE_CUSTOMER,
	}),
	manageLocationsReducer: GeneralReducers.CreateResultSetReducer<
		IManageLocations[]
	>({
		startAction: LocationManagementActions.SELF_SERVICE_GET_LOCATIONS,
	}),
	loadCustomerReducer:
		GeneralReducers.CreateLoaderReducer<INationalAccountCustomer>({
			startAction: LocationManagementActions.SELF_SERVICE_GET_CUSTOMER,
		}),
	loadDistributionCenterReducer: GeneralReducers.CreateLoaderReducer<
		IDistributionCenter[]
	>({
		startAction: LocationManagementActions.SELF_SERVICE_GET_OPERATING_COMPANIES,
	}),
	massImportLocationsReducer: GeneralReducers.CreateAsynchronousActionReducer({
		startAction: LocationManagementActions.SELF_SERVICE_MASS_IMPORT_LOCATIONS,
	}),
	isMassImportRequestInProgressReducer: GeneralReducers.CreateLoaderReducer({
		startAction:
			LocationManagementActions.SELF_SERVICE_MASS_IMPORT_LOCATIONS_IN_PROGRESS,
		clearPayloadWith:
			initialState.manageLocations.isMassImportRequestInProgress.payload,
	}),
};

export function syscoSelfServiceReducer(
	state = initialState,
	action
): ISyscoSelfServiceState {
	switch (action.type) {
		case CustomerManagementActions.SELF_SERVICE_FILTER_CHANGE:
		case CustomerManagementActions.SELF_SERVICE_FILTER_CHANGE_ERROR:
		case CustomerManagementActions.SELF_SERVICE_FILTER_CHANGE_SUCCESS:
			return {
				...state,
				manageCustomers: {
					...state.manageCustomers,
					searchString: action.payload,
				},
			};
		case CustomerManagementActions.SELF_SERVICE_GET_CUSTOMERS:
			return {
				...state,
				manageCustomers: {
					...state.manageCustomers,
					customers: _childReducers.manageCustomersReducer(
						state.manageCustomers.customers,
						action
					),
					currentPage: action.payload.currentPage,
					itemsPerPage: action.payload.itemsPerPage,
				},
			};
		case CustomerManagementActions.SELF_SERVICE_GET_CUSTOMERS_ERROR:
		case CustomerManagementActions.SELF_SERVICE_GET_CUSTOMERS_SUCCESS:
			return {
				...state,
				manageCustomers: {
					...state.manageCustomers,
					customers: _childReducers.manageCustomersReducer(
						state.manageCustomers.customers,
						action
					),
				},
			};

		case CustomerManagementActions.SELF_SERVICE_CREATE_CUSTOMER:
		case CustomerManagementActions.SELF_SERVICE_CREATE_CUSTOMER_ERROR:
		case CustomerManagementActions.SELF_SERVICE_CREATE_CUSTOMER_SUCCESS:
			return {
				...state,
				createCustomer: _childReducers.createCustomerReducer(
					state.createCustomer,
					action
				),
			};
		case LocationManagementActions.SELF_SERVICE_GET_LOCATIONS:
			return {
				...state,
				manageLocations: {
					...state.manageLocations,
					locations: _childReducers.manageLocationsReducer(
						state.manageLocations.locations,
						action
					),
					currentPage: action.payload.currentPage,
					itemsPerPage: action.payload.itemsPerPage,
				},
			};
		case LocationManagementActions.SELF_SERVICE_GET_LOCATIONS_ERROR:
		case LocationManagementActions.SELF_SERVICE_GET_LOCATIONS_SUCCESS:
			return {
				...state,
				manageLocations: {
					...state.manageLocations,
					locations: _childReducers.manageLocationsReducer(
						state.manageLocations.locations,
						action
					),
				},
			};
		case LocationManagementActions.SELF_SERVICE_GET_CUSTOMER:
		case LocationManagementActions.SELF_SERVICE_GET_CUSTOMER_ERROR:
		case LocationManagementActions.SELF_SERVICE_GET_CUSTOMER_SUCCESS:
			return {
				...state,
				createLocation: {
					...state.createLocation,
					customer: _childReducers.loadCustomerReducer(
						state.createLocation.customer,
						action
					),
				},
				manageLocations: {
					...state.manageLocations,
					customer: _childReducers.loadCustomerReducer(
						state.manageLocations.customer,
						action
					),
				},
			};
		case LocationManagementActions.SELF_SERVICE_GET_OPERATING_COMPANIES:
		case LocationManagementActions.SELF_SERVICE_GET_OPERATING_COMPANIES_SUCCESS:
		case LocationManagementActions.SELF_SERVICE_GET_OPERATING_COMPANIES_ERROR:
			return {
				...state,
				createLocation: {
					...state.createLocation,
					distributionCenters: _childReducers.loadDistributionCenterReducer(
						state.createLocation.distributionCenters,
						action
					),
				},
			};
		case LocationManagementActions.SELF_SERVICE_CREATE_LOCATION_SUCCESS:
			return {
				...state,
				createLocation: {
					...state.createLocation,
					inProcess: false,
					error: false,
				},
			};
		case LocationManagementActions.SELF_SERVICE_CREATE_LOCATION:
			return {
				...state,
				createLocation: {
					...state.createLocation,
					inProcess: true,
					error: false,
				},
			};
		case LocationManagementActions.SELF_SERVICE_CREATE_LOCATION_ERROR:
			return {
				...state,
				createLocation: {
					...state.createLocation,
					inProcess: false,
					error: true,
				},
			};
		case LocationManagementActions.SELF_SERVICE_MASS_IMPORT_LOCATIONS:
		case LocationManagementActions.SELF_SERVICE_MASS_IMPORT_LOCATIONS_SUCCESS:
		case LocationManagementActions.SELF_SERVICE_MASS_IMPORT_LOCATIONS_ERROR:
			return {
				...state,
				manageLocations: {
					...state.manageLocations,
					massImportLocations: _childReducers.massImportLocationsReducer(
						state.manageLocations.massImportLocations,
						action
					),
				},
			};
		case LocationManagementActions.SELF_SERVICE_MASS_IMPORT_LOCATIONS_IN_PROGRESS:
		case LocationManagementActions.SELF_SERVICE_MASS_IMPORT_LOCATIONS_IN_PROGRESS_SUCCESS:
		case LocationManagementActions.SELF_SERVICE_MASS_IMPORT_LOCATIONS_IN_PROGRESS_ERROR:
			return {
				...state,
				manageLocations: {
					...state.manageLocations,
					isMassImportRequestInProgress:
						_childReducers.isMassImportRequestInProgressReducer(
							state.manageLocations.isMassImportRequestInProgress,
							action
						),
				},
			};
		default:
			return state;
	}
}
